import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: 'website' | 'article'
}

// This site is served from TWO hostnames — software.garrettjohnlaw.com and
// ai.garrettjohnlaw.com — off one CloudFront distribution with identical content.
// Canonical URLs are therefore absolute and always on SITE_ORIGIN, never derived
// from window.location: whichever host served the request, search engines are told
// the software host is the original. Dropping that makes the two hosts compete for
// the same keywords and splits authority. Terraform's `alias_domain_names` is the
// other half of this contract.
const SITE_ORIGIN = 'https://software.garrettjohnlaw.com'

const defaultMeta = {
  title: 'Garrett John Law | AI Engineer — Production LLM Systems',
  description:
    'AI engineer specialising in production LLM systems: retrieval quality, agent orchestration, eval harnesses, observability, cost, and guardrails. Bedrock + Terraform on AWS.',
  image: '/og-image.jpg',
  url: SITE_ORIGIN,
}

export function SEO({
  title,
  description = defaultMeta.description,
  image = defaultMeta.image,
  url = defaultMeta.url,
  type = 'website',
}: SEOProps) {
  const fullTitle = title ? `${title} | Garrett John Law` : defaultMeta.title

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Per-route canonical (prerender de-dupes the static index.html one) */}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Person',
          '@id': 'https://garrettjohnlaw.com/#person',
          name: 'Garrett John Law',
          url: 'https://garrettjohnlaw.com',
          jobTitle: 'AI Engineer — Production LLM Systems',
          knowsAbout: [
            'Retrieval-augmented generation',
            'Agentic retrieval',
            'LLM evaluation harnesses',
            'LLM observability',
            'Inference cost optimization',
            'Prompt injection defense',
            'Multi-agent orchestration',
            'AWS Bedrock',
            'Terraform',
          ],
          sameAs: [
            'https://linkedin.com/in/garrettjohnlaw',
            'https://github.com/garrettjohnlaw',
            'https://instagram.com/garrettjohnlaw_',
            'https://www.facebook.com/garrett.john.law95',
            'https://www.youtube.com/@garrettjohnlawmusic',
            'https://music.garrettjohnlaw.com',
            'https://fuguelab.com',
            'https://fairmeadow.capital',
            'https://fusionstreetdigital.com',
            'https://classicallyminded.org',
          ],
        })}
      </script>
    </Helmet>
  )
}
